import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { ReactNode } from "react";
import { en } from "./locales/en/index.js";
import { ar } from "./locales/ar/index.js";

export type Locale = "en" | "ar";

type StringNode = { readonly [key: string]: string | StringNode };

type LeafKeys<T, Prefix extends string = ""> = {
  [K in keyof T]: T[K] extends string
    ? `${Prefix}${Extract<K, string>}`
    : T[K] extends StringNode
      ? LeafKeys<T[K], `${Prefix}${Extract<K, string>}.`>
      : never;
}[keyof T];

type Catalog = typeof en;
export type MessageKey = LeafKeys<Catalog>;

type LocaleCatalog = { readonly [K: string]: string | LocaleCatalog };

const catalogs: Record<Locale, LocaleCatalog> = {
  en: en as unknown as LocaleCatalog,
  ar: ar as unknown as LocaleCatalog,
};

const STORAGE_KEY = "grizzly.locale";

function resolve(catalog: LocaleCatalog, key: string): string | undefined {
  const parts = key.split(".");
  let node: string | LocaleCatalog | undefined = catalog;
  for (const part of parts) {
    if (node === undefined || typeof node === "string") return undefined;
    node = (node as LocaleCatalog)[part];
  }
  return typeof node === "string" ? node : undefined;
}

function interpolate(text: string, params?: Record<string, string | number>): string {
  if (!params) return text;
  return text.replace(/\{(\w+)\}/g, (match, name: string) => {
    const value = params[name];
    return value === undefined ? match : String(value);
  });
}

export interface I18nApi {
  locale: Locale;
  setLocale(locale: Locale): void;
  t(key: MessageKey, params?: Record<string, string | number>): string;
}

const I18nContext = createContext<I18nApi | null>(null);

function readInitialLocale(): Locale {
  if (typeof window === "undefined") return "en";
  const stored = window.localStorage.getItem(STORAGE_KEY);
  return stored === "ar" ? "ar" : "en";
}

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(readInitialLocale);

  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = locale === "ar" ? "rtl" : "ltr";
  }, [locale]);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    window.localStorage.setItem(STORAGE_KEY, next);
  }, []);

  const t = useCallback(
    (key: MessageKey, params?: Record<string, string | number>) => {
      const text = resolve(catalogs[locale], key) ?? resolve(catalogs.en, key) ?? key;
      return interpolate(text, params);
    },
    [locale]
  );

  const value = useMemo(() => ({ locale, setLocale, t }), [locale, setLocale, t]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nApi {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used inside LocaleProvider");
  return ctx;
}

export function useT() {
  return useI18n().t;
}

export function useLocale() {
  const { locale, setLocale } = useI18n();
  return { locale, setLocale };
}
